import { Box, Card, CardContent, CardHeader, Divider, Link, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { Country } from "country-state-city";
import { DateTime } from "luxon";
import { useParams } from "react-router";
import { TextViewer, isWhitespace } from "../../../../components";
import { Links } from "../../../../constants";
import { useGetUser } from "../../../../http";
import { JobTitleTooltipContent } from "../../components";
import { InfoOutlined } from "@mui/icons-material";

interface IParams {
  userId: string;
}

const UserAbout = () => {
  const { userId } = useParams<keyof IParams>() as IParams;
  const { data: user, isFetching } = useGetUser(userId);

  function getLocationFormattedString() {
    if (user) {
      var countryName = Country.getCountryByCode(user.countryCode ?? "")?.name;
      var result = "";

      if (user.cityName) {
        result = `${user.cityName}, ${user.stateCode}, `;
      }

      if (countryName) {
        result += countryName;
      }
      return result;
    }

    return;
  }

  const formattedLocationString = getLocationFormattedString();

  return (
    <Card>
      <CardHeader title={<Typography variant="h6">About</Typography>} />
      <Divider />
      <CardContent>
        <Stack spacing={1}>
          {isFetching && (
            <>
              {[...Array(3)].map((_, i) => (
                <Box key={i}>
                  <Skeleton width={100} height={17} />
                  <Skeleton width={200} height={24} />
                </Box>
              ))}
            </>
          )}

          {!isFetching && user != null && (
            <>
              <Box>
                <Stack direction="row" spacing={0.25}>
                  <Typography variant="label">Job Title</Typography>
                  <Tooltip title={<JobTitleTooltipContent />}>
                    <InfoOutlined sx={{ color: "text.secondary", alignSelf: "center" }} fontSize="small" />
                  </Tooltip>
                </Stack>
                <Divider />
                {user?.jobTitle && !isWhitespace(user.jobTitle) ? (
                  <Typography variant="body1">{user.jobTitle}</Typography>
                ) : (
                  <Typography variant="placeholder">Nothing here yet.</Typography>
                )}
              </Box>

              {user?.bio && !isWhitespace(user.bio) && (
                <Box>
                  <Typography variant="overline">Bio</Typography>
                  <TextViewer value={user.bio} />
                </Box>
              )}

              <Box>
                <Typography variant="overline">Joined Date</Typography>
                {user.joinedDate ? (
                  <Typography variant="body1">{user.joinedDate?.toLocaleString(DateTime.DATE_FULL)}</Typography>
                ) : (
                  <Typography variant="placeholder">Nothing here yet.</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="overline">Location</Typography>
                {!isWhitespace(formattedLocationString ?? "") ? (
                  <Typography>{formattedLocationString}</Typography>
                ) : (
                  <Typography variant="placeholder">Nothing here yet.</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="overline">People Lead</Typography>
                {user.peopleLead ? (
                  <Link onClick={(e) => e.stopPropagation()} href={Links.User(user.peopleLead.userId)}>
                    {`${user.peopleLead.displayName}${user.peopleLead.enabled ? "" : " (deactivated)"}`}
                  </Link>
                ) : (
                  <Typography variant="placeholder">Nothing here yet.</Typography>
                )}
              </Box>

              {user.pronouns && (
                <Box>
                  <Typography variant="overline">Pronouns</Typography>
                  <Typography variant="body1">{user.pronouns}</Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export { UserAbout };
